import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const TermsConditions = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms & Conditions - Tagnetica</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="bingbot" content="noindex, nofollow" />
        <meta name="description" content="Terms & Conditions for Tagnetica" />
      </Helmet>
      
      <Header />
      
      <main className="pt-16">
        <div className="container mx-auto px-6 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl font-bold text-text-primary mb-6">Terms & Conditions</h1>
              <p className="text-lg text-text-secondary">
                Last updated: {new Date().toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>

            {/* Key Points Summary */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 mb-12">
              <h2 className="text-2xl font-semibold text-text-primary mb-4">Key Points Summary</h2>
              <div className="grid md:grid-cols-2 gap-4 text-text-secondary">
                <ul className="space-y-2">
                  <li>• Services include tag management setup, analytics implementation, and reporting</li>
                  <li>• 99.9% uptime SLA with defined support hours</li>
                  <li>• Client must provide site access and obtain user consents</li>
                  <li>• Payment terms: 30 days from invoice date</li>
                </ul>
                <ul className="space-y-2">
                  <li>• 12-month initial term with auto-renewal</li>
                  <li>• Client owns their data, Tagnetica owns the software</li>
                  <li>• Liability capped at fees paid in prior 12 months</li>
                  <li>• Governed by English law, London courts</li>
                </ul>
              </div>
            </div>

            {/* Table of Contents */}
            <div className="bg-accent/30 border border-primary/20 rounded-xl p-8 mb-12">
              <h2 className="text-2xl font-semibold text-text-primary mb-6">Table of Contents</h2>
              <nav className="space-y-2">
                <button 
                  onClick={() => scrollToSection('definitions')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  1. Definitions & Scope of Services
                </button>
                <button 
                  onClick={() => scrollToSection('client-obligations')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  2. Client Obligations
                </button>
                <button 
                  onClick={() => scrollToSection('fees-payment')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  3. Fees & Payment
                </button>
                <button 
                  onClick={() => scrollToSection('term-renewal')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  4. Term, Renewal & Termination
                </button>
                <button 
                  onClick={() => scrollToSection('intellectual-property')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  5. Intellectual Property
                </button>
                <button 
                  onClick={() => scrollToSection('confidentiality')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  6. Confidentiality
                </button>
                <button 
                  onClick={() => scrollToSection('warranties')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  7. Warranties & Disclaimers
                </button>
                <button 
                  onClick={() => scrollToSection('liability')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  8. Limitation of Liability
                </button>
                <button 
                  onClick={() => scrollToSection('indemnification')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  9. Indemnification
                </button>
                <button 
                  onClick={() => scrollToSection('governing-law')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  10. Governing Law & Dispute Resolution
                </button>
                <button 
                  onClick={() => scrollToSection('changes')}
                  className="block text-left text-primary hover:text-primary/80 underline transition-colors"
                >
                  11. Changes to Terms
                </button>
              </nav>
            </div>

            {/* Content Sections */}
            <div className="space-y-12">
              {/* 1. Definitions & Scope of Services */}
              <section id="definitions">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">1. Definitions & Scope of Services</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Definitions</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>"Services"</strong> means tag management setup, analytics implementation, reporting, and related consulting services provided by Tagnetica</li>
                      <li><strong>"Client"</strong> means the individual or entity purchasing Services from Tagnetica</li>
                      <li><strong>"Deliverables"</strong> means the specific outputs, reports, and configurations provided as part of the Services</li>
                      <li><strong>"SLA"</strong> means Service Level Agreement as defined in this document</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Scope of Services</h3>
                    <p>Tagnetica provides the following services:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Tag Management Setup:</strong> Google Tag Manager container configuration, tag implementation, and testing</li>
                      <li><strong>Analytics Implementation:</strong> Google Analytics 4 (GA4) and Matomo setup, configuration, and validation</li>
                      <li><strong>Reporting & Auditing:</strong> Analytics audits, performance reports, and optimization recommendations</li>
                      <li><strong>Consulting:</strong> Strategic advice on analytics implementation and data strategy</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Service Level Agreement (SLA)</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Uptime:</strong> 99.9% availability for hosted services</li>
                      <li><strong>Response Time:</strong> Initial response within 24 hours for support requests</li>
                      <li><strong>Support Hours:</strong> Monday to Friday, 9:00 AM to 5:00 PM GMT (excluding UK bank holidays)</li>
                      <li><strong>Emergency Support:</strong> Available for critical issues outside normal hours</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 2. Client Obligations */}
              <section id="client-obligations">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">2. Client Obligations</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Site Access & Permissions</h3>
                    <p>The Client agrees to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide necessary access credentials to their website and analytics platforms</li>
                      <li>Grant Tagnetica permission to implement and test tracking configurations</li>
                      <li>Ensure Tagnetica has administrative access to Google Tag Manager, GA4, or Matomo as required</li>
                      <li>Maintain access permissions throughout the service period</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Legal Compliance</h3>
                    <p>The Client is responsible for:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Obtaining all necessary consents from end users for cookie and analytics tracking</li>
                      <li>Ensuring compliance with applicable data protection laws (GDPR, CCPA, etc.)</li>
                      <li>Maintaining appropriate privacy policies and cookie notices</li>
                      <li>Providing accurate information about their data collection practices</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Cooperation</h3>
                    <p>The Client shall:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Respond promptly to Tagnetica's requests for information or decisions</li>
                      <li>Provide timely feedback on deliverables and implementations</li>
                      <li>Designate a primary contact person for communications</li>
                      <li>Notify Tagnetica of any changes to their website or business requirements</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 3. Fees & Payment */}
              <section id="fees-payment">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">3. Fees & Payment</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Pricing Structure</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Setup Fees:</strong> One-time charges for initial implementation and configuration</li>
                      <li><strong>Monthly/Annual Subscriptions:</strong> Ongoing service fees for maintenance and support</li>
                      <li><strong>Usage-Based Fees:</strong> Charges based on data volume or feature usage where applicable</li>
                      <li><strong>Consulting Rates:</strong> Hourly or project-based fees for additional consulting services</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Payment Terms</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Invoicing:</strong> Invoices will be issued monthly in advance for subscription services</li>
                      <li><strong>Payment Due:</strong> All invoices are due within 30 days of invoice date</li>
                      <li><strong>Late Payment:</strong> Late payments may incur interest at 1.5% per month or the maximum rate permitted by law</li>
                      <li><strong>Currency:</strong> All fees are quoted and payable in British Pounds Sterling (GBP)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Taxes</h3>
                    <p>All fees are exclusive of VAT and other applicable taxes. The Client is responsible for paying any taxes, duties, or similar charges imposed by any government authority.</p>
                  </div>
                </div>
              </section>

              {/* 4. Term, Renewal & Termination */}
              <section id="term-renewal">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">4. Term, Renewal & Termination</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Initial Term</h3>
                    <p>The initial term of these Terms is 12 months from the effective date, unless otherwise specified in the service agreement.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Renewal</h3>
                    <p>These Terms will automatically renew for successive 12-month periods unless either party provides written notice of non-renewal at least 30 days before the end of the current term.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Termination for Convenience</h3>
                    <p>Either party may terminate these Terms for convenience by providing 30 days' written notice to the other party.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Termination for Breach</h3>
                    <p>Either party may terminate these Terms immediately if the other party:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Materially breaches these Terms and fails to cure such breach within 14 days of written notice</li>
                      <li>Becomes insolvent, bankrupt, or enters into liquidation or receivership</li>
                      <li>Engages in conduct that materially harms the other party's business or reputation</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Post-Termination</h3>
                    <p>Upon termination:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Tagnetica will return or destroy all Client data in its possession within 30 days</li>
                      <li>The Client will pay all outstanding fees for services rendered</li>
                      <li>Each party will return or destroy the other's confidential information</li>
                      <li>The Client will have 30 days to export their data from Tagnetica's systems</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 5. Intellectual Property */}
              <section id="intellectual-property">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">5. Intellectual Property</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Tagnetica's Intellectual Property</h3>
                    <p>Tagnetica retains ownership of:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>All software, scripts, tools, and methodologies developed by Tagnetica</li>
                      <li>Proprietary tracking configurations and implementation techniques</li>
                      <li>Documentation, templates, and training materials created by Tagnetica</li>
                      <li>Any improvements or modifications to Tagnetica's existing intellectual property</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Client's Intellectual Property</h3>
                    <p>The Client retains ownership of:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Their raw data, analytics data, and business information</li>
                      <li>Custom reports and dashboards created specifically for the Client</li>
                      <li>Client-specific configurations and customizations</li>
                      <li>Any intellectual property provided to Tagnetica by the Client</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">License</h3>
                    <p>Tagnetica grants the Client a non-exclusive, non-transferable license to use the Services and Deliverables solely for the Client's internal business purposes during the term of these Terms.</p>
                  </div>
                </div>
              </section>

              {/* 6. Confidentiality */}
              <section id="confidentiality">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">6. Confidentiality</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Definition</h3>
                    <p>"Confidential Information" means any non-public information disclosed by one party to the other, including but not limited to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Business plans, strategies, and financial information</li>
                      <li>Technical specifications and implementation details</li>
                      <li>Customer data and analytics information</li>
                      <li>Proprietary methodologies and trade secrets</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Obligations</h3>
                    <p>Each party agrees to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Keep the other party's Confidential Information strictly confidential</li>
                      <li>Use Confidential Information solely for the purpose of performing obligations under these Terms</li>
                      <li>Not disclose Confidential Information to any third party without prior written consent</li>
                      <li>Implement appropriate security measures to protect Confidential Information</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Duration</h3>
                    <p>Confidentiality obligations survive termination of these Terms and continue for a period of 2 years from the date of disclosure.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Exceptions</h3>
                    <p>Confidentiality obligations do not apply to information that:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Was already known to the receiving party before disclosure</li>
                      <li>Becomes publicly available through no fault of the receiving party</li>
                      <li>Is independently developed by the receiving party without use of the confidential information</li>
                      <li>Is required to be disclosed by law or court order</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 7. Warranties & Disclaimers */}
              <section id="warranties">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">7. Warranties & Disclaimers</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Tagnetica Warranties</h3>
                    <p>Tagnetica warrants that:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>It has the right and authority to provide the Services</li>
                      <li>It will perform the Services in a professional and workmanlike manner</li>
                      <li>It will use commercially reasonable efforts to meet the SLA commitments</li>
                      <li>It will comply with applicable laws in providing the Services</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Client Warranties</h3>
                    <p>The Client warrants that:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>It has the right to provide Tagnetica with access to its systems and data</li>
                      <li>It will comply with all applicable laws and regulations</li>
                      <li>It has obtained all necessary consents for data processing</li>
                      <li>It will not use the Services for any illegal or unauthorized purpose</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Disclaimers</h3>
                    <p>EXCEPT AS EXPRESSLY PROVIDED IN THESE TERMS, ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY.</p>
                    <p>Tagnetica specifically disclaims:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Any warranties regarding third-party services, APIs, or platforms</li>
                      <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
                      <li>Warranties that the Services will be uninterrupted, error-free, or secure</li>
                      <li>Any warranties regarding the accuracy or completeness of analytics data</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 8. Limitation of Liability */}
              <section id="liability">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">8. Limitation of Liability</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Liability Cap</h3>
                    <p>In no event shall Tagnetica's total liability to the Client exceed the amount of fees paid by the Client to Tagnetica in the 12 months preceding the claim.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Excluded Damages</h3>
                    <p>Neither party shall be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Loss of profits, revenue, or business opportunities</li>
                      <li>Loss of data or information</li>
                      <li>Business interruption or downtime</li>
                      <li>Reputational damage</li>
                      <li>Any damages arising from third-party services or platforms</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Exceptions</h3>
                    <p>The limitations of liability do not apply to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Breaches of confidentiality obligations</li>
                      <li>Intellectual property infringement</li>
                      <li>Personal injury or death caused by negligence</li>
                      <li>Any liability that cannot be limited by law</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 9. Indemnification */}
              <section id="indemnification">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">9. Indemnification</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Client Indemnification</h3>
                    <p>The Client shall indemnify and hold harmless Tagnetica from and against any claims, damages, losses, and expenses arising from:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>The Client's breach of these Terms</li>
                      <li>The Client's failure to obtain necessary consents or permissions</li>
                      <li>The Client's violation of applicable laws or regulations</li>
                      <li>The Client's use of the Services in violation of these Terms</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Tagnetica Indemnification</h3>
                    <p>Tagnetica shall indemnify and hold harmless the Client from and against any claims, damages, losses, and expenses arising from:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Tagnetica's breach of these Terms</li>
                      <li>Tagnetica's violation of applicable laws or regulations</li>
                      <li>Tagnetica's infringement of third-party intellectual property rights</li>
                      <li>Tagnetica's gross negligence or willful misconduct</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Indemnification Process</h3>
                    <p>The indemnified party shall:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Promptly notify the indemnifying party of any claim</li>
                      <li>Allow the indemnifying party to control the defense and settlement</li>
                      <li>Provide reasonable cooperation in the defense</li>
                      <li>Not settle any claim without the indemnifying party's written consent</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 10. Governing Law & Dispute Resolution */}
              <section id="governing-law">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">10. Governing Law & Dispute Resolution</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Governing Law</h3>
                    <p>These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law principles.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Jurisdiction</h3>
                    <p>Any legal proceedings arising from these Terms shall be brought exclusively in the courts of England and Wales, and both parties consent to the jurisdiction of such courts.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Dispute Resolution</h3>
                    <p>Before pursuing legal action, the parties agree to:</p>
                    <ol className="list-decimal pl-6 space-y-2">
                      <li><strong>Direct Negotiation:</strong> Attempt to resolve disputes through good faith negotiations for 30 days</li>
                      <li><strong>Mediation:</strong> If negotiation fails, participate in mediation with a neutral mediator</li>
                      <li><strong>Arbitration:</strong> If mediation fails, submit to binding arbitration under the rules of the London Court of International Arbitration (LCIA)</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Emergency Relief</h3>
                    <p>Nothing in this section prevents either party from seeking emergency injunctive relief from a court of competent jurisdiction to prevent irreparable harm.</p>
                  </div>
                </div>
              </section>

              {/* 11. Changes to Terms */}
              <section id="changes">
                <h2 className="text-3xl font-semibold text-text-primary mb-6">11. Changes to Terms</h2>
                <div className="prose prose-lg max-w-none text-text-secondary space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Modification Process</h3>
                    <p>Tagnetica may modify these Terms from time to time by:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Providing 30 days' written notice of material changes</li>
                      <li>Posting updated Terms on the Tagnetica website</li>
                      <li>Sending email notification to the Client's designated contact</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Acceptance of Changes</h3>
                    <p>Continued use of the Services after the effective date of any changes constitutes acceptance of the modified Terms. If the Client does not agree to the changes, they may terminate these Terms by providing written notice within 30 days of the change notification.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Material Changes</h3>
                    <p>Material changes include but are not limited to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Changes to pricing or payment terms</li>
                      <li>Modifications to service levels or availability</li>
                      <li>Changes to data handling or privacy practices</li>
                      <li>Modifications to liability limitations or indemnification terms</li>
                    </ul>
                  </div>

                  <div className="bg-accent/30 border border-primary/20 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Contact Us</h3>
                    <p className="text-text-secondary">
                      If you have any questions about these Terms & Conditions, please contact us:
                    </p>
                    <ul className="list-none space-y-2 mt-3">
                      <li><strong>Email:</strong> hello@tagnetica.co.uk</li>
                      <li><strong>Website:</strong> https://tagimplementation.tagnetica.co.uk</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditions; 